import { useCallback, useEffect, useRef, useState } from "react";

const HIGHLIGHT_DURATION = 5000;

/**
 * Drives the text search above the "Choose other parameters" tabs.
 *
 * Typing matches the first parameter (across all tabs, in order) whose name
 * contains the query. When a match is found we switch to its tab, scroll it
 * into view and highlight it. The highlight clears after 5s or as soon as the
 * user checks that parameter.
 *
 * @param {Array} paramsList - grouped parameters (each group has `actions`).
 * @param {Function} l - lexicon translate function.
 * @param {Array} checkedIds - currently selected action ids.
 */
const useParameterSearch = (paramsList, l, checkedIds) => {
  const [query, setQuery] = useState("");
  const [selectedTabIndex, setSelectedTabIndex] = useState(0);
  const [highlightedActionId, setHighlightedActionId] = useState(null);

  const highlightedRef = useRef(null);
  const tabListRef = useRef(null);
  const timerRef = useRef(null);
  const searchInputRef = useRef(null);

  // Switching tabs / scrolling the match into view makes react-tabs move focus
  // to the selected tab. Restore focus to the search input so the user can keep
  // typing without re-clicking it.
  const restoreSearchFocus = useCallback(() => {
    const input = searchInputRef.current;
    if (!input || document.activeElement === input) return;
    requestAnimationFrame(() => {
      const el = searchInputRef.current;
      if (el && document.activeElement !== el) {
        el.focus({ preventScroll: true });
      }
    });
  }, []);

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const getActionName = useCallback(
    (action) => (l(action.lexic_id) || action.title || "").toLowerCase(),
    [l]
  );

  const handleSearchChange = useCallback(
    (value) => {
      setQuery(value);

      const term = value.trim().toLowerCase();
      clearTimer();

      if (!term || !Array.isArray(paramsList)) {
        setHighlightedActionId(null);
        return;
      }

      let matchGroupIndex = -1;
      let matchActionId = null;

      for (let i = 0; i < paramsList.length; i += 1) {
        const action = paramsList[i]?.actions?.find((a) =>
          getActionName(a).includes(term)
        );
        if (action) {
          matchGroupIndex = i;
          matchActionId = action.id;
          break;
        }
      }

      if (matchActionId != null) {
        setSelectedTabIndex(matchGroupIndex);
        setHighlightedActionId(matchActionId);
        timerRef.current = setTimeout(() => {
          setHighlightedActionId(null);
          timerRef.current = null;
        }, HIGHLIGHT_DURATION);
      } else {
        setHighlightedActionId(null);
      }
    },
    [paramsList, getActionName, clearTimer]
  );

  // Scroll the matched parameter into view once it renders.
  useEffect(() => {
    if (highlightedActionId != null && highlightedRef.current) {
      highlightedRef.current.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
      restoreSearchFocus();
    }
  }, [highlightedActionId, restoreSearchFocus]);

  // Keep the active tab visible within the horizontally scrollable tab list.
  useEffect(() => {
    const selectedTab = tabListRef.current?.querySelector(
      ".react-tabs__tab--selected"
    );
    if (selectedTab) {
      selectedTab.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }
    restoreSearchFocus();
  }, [selectedTabIndex, restoreSearchFocus]);

  // Clear the highlight as soon as the user checks the matched parameter.
  useEffect(() => {
    if (
      highlightedActionId != null &&
      Array.isArray(checkedIds) &&
      checkedIds.includes(highlightedActionId)
    ) {
      clearTimer();
      setHighlightedActionId(null);
    }
  }, [checkedIds, highlightedActionId, clearTimer]);

  useEffect(() => clearTimer, [clearTimer]);

  return {
    query,
    handleSearchChange,
    selectedTabIndex,
    setSelectedTabIndex,
    highlightedActionId,
    highlightedRef,
    tabListRef,
    searchInputRef,
  };
};

export default useParameterSearch;
